import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Installs the Maps JavaScript bootstrap and downloads the script only when a
 * caller asks for a library. Opening a session no longer contacts Google.
 */
@Injectable({
	providedIn: 'root',
})
export class GoogleMapsLoaderService {
	private installed = false;
	private scriptPromise: Promise<void> | null = null;
	private readonly libraries = new Set<string>();

	load(...libraries: string[]): Promise<void> {
		this.installBootstrap();
		const maps = (window as unknown as { google: { maps: { importLibrary: (name: string) => Promise<unknown> } } }).google.maps;
		return Promise.all(libraries.map((library) => maps.importLibrary(library))).then(() => undefined);
	}

	private installBootstrap(): void {
		const root = window as unknown as { google?: { maps?: { importLibrary?: (name: string) => Promise<unknown> } } };
		if (typeof root.google?.maps?.importLibrary === 'function') {
			this.installed = true;
			return;
		}
		if (this.installed) {
			return;
		}
		this.installed = true;

		root.google = root.google || {};
		root.google.maps = root.google.maps || {};
		const maps = root.google.maps;
		maps.importLibrary = (library: string) => {
			this.libraries.add(library);
			return this.beginScriptLoad().then(() => maps.importLibrary(library));
		};
	}

	private beginScriptLoad(): Promise<void> {
		if (this.scriptPromise) {
			return this.scriptPromise;
		}
		this.scriptPromise = (async () => {
			await Promise.resolve();
			await new Promise<void>((resolve, reject) => {
				const maps = (window as unknown as { google: { maps: { __ib__?: () => void } } }).google.maps;
				const script = document.createElement('script');
				const search = new URLSearchParams();
				search.set('key', environment.googleAPI);
				search.set('v', 'weekly');
				search.set('libraries', [...this.libraries].join(','));
				search.set('callback', 'google.maps.__ib__');
				maps.__ib__ = () => resolve();
				script.async = true;
				script.src = `https://maps.googleapis.com/maps/api/js?${search.toString()}`;
				script.onerror = () => {
					this.scriptPromise = null;
					reject(new Error('Google Maps JavaScript API could not load.'));
				};
				document.head.appendChild(script);
			});
		})();
		return this.scriptPromise;
	}
}
